import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";

import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import { useAuth } from "context/AuthContext";
import { get, post } from "lib/api";
import { isCustomerRole, isDistributorRole } from "lib/portalRoles";
import { getTextLocale, setTextLocale } from "lib/storefront";
import { useQuery } from "@tanstack/react-query";

const PENDING_KEY = "pendingValveBodyRegistration";

const COLORS = {
  canvas: "#F7F8FA",
  surface: "#FFFFFF",
  ink: "#0F0F12",
  muted: "#55575C",
  subtle: "#6E7278",
  line: "rgba(15, 15, 18, 0.1)",
  accent: "#B54708",
  accentHover: "#953A05",
  accentSoft: "rgba(181, 71, 8, 0.08)",
  accentBorder: "rgba(181, 71, 8, 0.22)",
  success: "#147A5C",
  successSoft: "rgba(20, 122, 92, 0.08)",
  danger: "#A51812",
  dangerSoft: "rgba(215, 38, 30, 0.07)",
};

const COPY = {
  "en-US": {
    stepOf: "Step {current} of {total}",
    tagLabel: "Tag {tag}",
    language: "Language",
    english: "English",
    spanish: "Español",
    step1Title: "What are you doing?",
    step1Subtitle: "Choose how this valve body should be registered.",
    installing: "Installing",
    installingHint: "Register this unit to a vehicle for warranty.",
    selling: "Selling",
    sellingHint: "Confirm this unit was sold by a distributor.",
    continue: "Continue",
    back: "Back",
    step2InstallTitle: "Vehicle details",
    step2InstallSubtitle: "Enter the vehicle this valve body is being installed in.",
    step2SoldTitle: "Confirm sale",
    step2SoldSubtitle: "You are confirming this valve body was sold to a customer.",
    required: "Required",
    optional: "Optional",
    vehicleYear: "Vehicle year",
    vehicleMake: "Make",
    vehicleModel: "Model",
    vehicleTrim: "Trim",
    vehicleVin: "VIN",
    notes: "Notes",
    authTitle: "Sign in to finish",
    authInstallSubtitle: "Create or sign in to a customer account to register this unit.",
    authSoldSubtitle: "Create or sign in to a distributor account to confirm this sale.",
    signIn: "Sign in",
    createCustomer: "Create customer account",
    createDistributor: "Create distributor account",
    confirmTitle: "Confirm registration",
    confirmInstall: "Would you like to register valve body tag {tag}?",
    confirmSold: "Would you like to confirm sale for valve body tag {tag}?",
    confirmInstallHint: "This links the unit to your account for warranty support.",
    confirmSoldHint: "This records the distributor sale for this unit.",
    yes: "Yes, register",
    yesSold: "Yes, confirm sold",
    no: "No",
    registering: "Registering…",
    saving: "Saving…",
    successSold: "Distributor sold confirmation saved.",
    successInstall: "Valve body registered successfully.",
    errorGeneric: "Unable to complete this valve-body registration flow.",
    wrongCustomer: "This flow is only available to customer accounts.",
    wrongDistributor: "This flow is only available to distributor accounts.",
    blockedCustomer: "Your membership cannot register this item.",
    blockedDistributor: "Your membership cannot confirm sold units.",
    nativeLaunch:
      "Opening the app. If it is not installed, the app store will open.",
    reviewTag: "Valve body tag",
    reviewMode: "Action",
    reviewModeInstall: "Install registration",
    reviewModeSold: "Distributor sold",
  },
  "es-MX": {
    stepOf: "Paso {current} de {total}",
    tagLabel: "Etiqueta {tag}",
    language: "Idioma",
    english: "English",
    spanish: "Español",
    step1Title: "¿Qué está haciendo?",
    step1Subtitle: "Elija cómo registrar este cuerpo de válvula.",
    installing: "Instalando",
    installingHint: "Registre esta unidad en un vehículo para la garantía.",
    selling: "Vendiendo",
    sellingHint: "Confirme que esta unidad fue vendida por un distribuidor.",
    continue: "Continuar",
    back: "Atrás",
    step2InstallTitle: "Datos del vehículo",
    step2InstallSubtitle: "Ingrese el vehículo donde se instala este cuerpo de válvula.",
    step2SoldTitle: "Confirmar venta",
    step2SoldSubtitle: "Está confirmando que este cuerpo de válvula se vendió a un cliente.",
    required: "Obligatorio",
    optional: "Opcional",
    vehicleYear: "Año del vehículo",
    vehicleMake: "Marca",
    vehicleModel: "Modelo",
    vehicleTrim: "Versión",
    vehicleVin: "VIN",
    notes: "Notas",
    authTitle: "Inicie sesión para terminar",
    authInstallSubtitle:
      "Cree o inicie sesión en una cuenta de cliente para registrar esta unidad.",
    authSoldSubtitle:
      "Cree o inicie sesión en una cuenta de distribuidor para confirmar esta venta.",
    signIn: "Iniciar sesión",
    createCustomer: "Crear cuenta de cliente",
    createDistributor: "Crear cuenta de distribuidor",
    confirmTitle: "Confirmar registro",
    confirmInstall: "¿Desea registrar el cuerpo de válvula con etiqueta {tag}?",
    confirmSold: "¿Desea confirmar la venta del cuerpo de válvula con etiqueta {tag}?",
    confirmInstallHint: "Esto vincula la unidad a su cuenta para soporte de garantía.",
    confirmSoldHint: "Esto registra la venta del distribuidor para esta unidad.",
    yes: "Sí, registrar",
    yesSold: "Sí, confirmar venta",
    no: "No",
    registering: "Registrando…",
    saving: "Guardando…",
    successSold: "Confirmación de venta del distribuidor guardada.",
    successInstall: "Cuerpo de válvula registrado correctamente.",
    errorGeneric: "No se pudo completar el registro de este cuerpo de válvula.",
    wrongCustomer: "Este flujo solo está disponible para cuentas de cliente.",
    wrongDistributor: "Este flujo solo está disponible para cuentas de distribuidor.",
    blockedCustomer: "Su membresía no puede registrar este artículo.",
    blockedDistributor: "Su membresía no puede confirmar unidades vendidas.",
    nativeLaunch:
      "Abriendo la app. Si no está instalada, se abrirá la tienda de aplicaciones.",
    reviewTag: "Etiqueta del cuerpo de válvula",
    reviewMode: "Acción",
    reviewModeInstall: "Registro de instalación",
    reviewModeSold: "Venta de distribuidor",
  },
};

function normalizeUiLocale(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "es" || raw.startsWith("es-")) return "es-MX";
  return "en-US";
}

function formatCopy(template, vars = {}) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] == null ? "" : String(vars[key])
  );
}

function readPendingRegistration() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writePendingRegistration(payload) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function clearPendingRegistration() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

function LanguageToggle({ locale, onChange, labels }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        p: 0.35,
        borderRadius: 999,
        bgcolor: "rgba(15,15,18,0.05)",
        border: `1px solid ${COLORS.line}`,
      }}
      role="group"
      aria-label={labels.language}
    >
      {[
        { key: "en-US", label: labels.english },
        { key: "es-MX", label: labels.spanish },
      ].map((option) => {
        const active = locale === option.key;
        return (
          <Box
            key={option.key}
            component="button"
            type="button"
            onClick={() => onChange(option.key)}
            sx={{
              border: 0,
              cursor: "pointer",
              px: 1.35,
              py: 0.55,
              borderRadius: 999,
              fontSize: "0.78rem",
              fontWeight: 800,
              letterSpacing: "0.02em",
              bgcolor: active ? COLORS.ink : "transparent",
              color: active ? "#fff" : COLORS.muted,
              transition: "background-color 160ms ease, color 160ms ease",
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

LanguageToggle.propTypes = {
  locale: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    language: PropTypes.string.isRequired,
    english: PropTypes.string.isRequired,
    spanish: PropTypes.string.isRequired,
  }).isRequired,
};

function ChoiceCard({ active, title, hint, icon, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        borderRadius: 2.5,
        border: `1.5px solid ${active ? COLORS.accentBorder : COLORS.line}`,
        bgcolor: active ? COLORS.accentSoft : COLORS.surface,
        p: 2,
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 1.5,
        alignItems: "center",
        boxShadow: active ? "0 10px 28px rgba(181,71,8,0.12)" : "none",
        transition: "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          borderColor: COLORS.accentBorder,
          bgcolor: COLORS.accentSoft,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: active ? COLORS.accent : "rgba(15,15,18,0.06)",
          color: active ? "#fff" : COLORS.ink,
        }}
      >
        <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
      </Box>
      <Box>
        <MDTypography
          variant="button"
          sx={{ color: COLORS.ink, fontWeight: 800, display: "block", mb: 0.25 }}
        >
          {title}
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: COLORS.muted, lineHeight: 1.35 }}>
          {hint}
        </MDTypography>
      </Box>
    </Box>
  );
}

ChoiceCard.propTypes = {
  active: PropTypes.bool,
  title: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

ChoiceCard.defaultProps = {
  active: false,
};

function FieldLabel({ children, optionalLabel, optional }) {
  return (
    <MDTypography
      variant="caption"
      sx={{
        color: COLORS.muted,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        mb: 0.65,
        display: "block",
      }}
    >
      {children}
      {optional ? (
        <Box component="span" sx={{ ml: 0.75, textTransform: "none", fontWeight: 600, opacity: 0.75 }}>
          · {optionalLabel}
        </Box>
      ) : null}
    </MDTypography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  optionalLabel: PropTypes.string,
  optional: PropTypes.bool,
};

FieldLabel.defaultProps = {
  optionalLabel: "",
  optional: false,
};

export default function RegisterValveBody() {
  const { token, pluginRole, authLoading } = useAuth();
  const navigate = useNavigate();
  const { tag } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = String(searchParams.get("mode") || "")
    .trim()
    .toLowerCase();
  const launchedFromQr = String(searchParams.get("qr") || "").trim() === "1";
  const forceWebMode = String(searchParams.get("web") || "").trim() === "1";
  const allowNativeLaunch =
    String(searchParams.get("native") || "").trim() === "1";
  const wantsConfirm = String(searchParams.get("confirm") || "").trim() === "1";
  const wantsAuth = String(searchParams.get("auth") || "").trim() === "1";
  const fromAccount = String(searchParams.get("from") || "").trim() === "account";
  /** Existing JWT — account / already-signed-in users skip login & signup. */
  const hasSession = Boolean(token);
  const qrLaunchMode = launchedFromQr && allowNativeLaunch && !forceWebMode;
  const distributorMode = mode === "distributor-sold";
  const installingMode = mode === "installing";
  const hasIntent = distributorMode || installingMode;
  const role = String(pluginRole || "").toLowerCase();
  const isCustomer = isCustomerRole(role);
  const isDistributor = isDistributorRole(role);
  const roleReady = !authLoading && pluginRole != null;

  const [uiLocale, setUiLocale] = useState(() => normalizeUiLocale(getTextLocale()));
  const copy = COPY[uiLocale] || COPY["en-US"];
  const t = (key, vars) => formatCopy(copy[key] || COPY["en-US"][key] || key, vars);

  const [form, setForm] = useState(() => {
    const pending = readPendingRegistration();
    if (pending?.tag && String(pending.tag) === String(tag) && pending.form) {
      return {
        vehicleYear: pending.form.vehicleYear || "",
        vehicleMake: pending.form.vehicleMake || "",
        vehicleModel: pending.form.vehicleModel || "",
        vehicleTrim: pending.form.vehicleTrim || "",
        vehicleVin: pending.form.vehicleVin || "",
        notes: pending.form.notes || "",
      };
    }
    return {
      vehicleYear: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleTrim: "",
      vehicleVin: "",
      notes: "",
    };
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [launchingNative, setLaunchingNative] = useState(false);
  const [redirectingTenant, setRedirectingTenant] = useState(false);

  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities", "register-flow"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && (isCustomer || isDistributor),
    staleTime: 15000,
  });
  const capabilities = meQuery.data?.capabilities || {};

  const phase = useMemo(() => {
    if (!hasIntent) return "intent";
    if (wantsConfirm) return "confirm";
    // Logged-in (incl. account → scan): never show login/signup; go to confirm.
    if (hasSession && wantsAuth) return "confirm";
    // Hydrating an existing JWT — wait instead of flashing the auth gate.
    if (wantsAuth && authLoading) return "session";
    // Logged-out public QR still needs sign-in / create account.
    if (wantsAuth && !hasSession) return "auth";
    return "details";
  }, [hasIntent, wantsConfirm, wantsAuth, hasSession, authLoading]);

  const stepNumber = phase === "intent" ? 1 : 2;

  const currentPath = useMemo(() => {
    const params = new URLSearchParams();
    if (distributorMode) params.set("mode", "distributor-sold");
    if (installingMode) params.set("mode", "installing");
    if (launchedFromQr) params.set("qr", "1");
    if (fromAccount) params.set("from", "account");
    params.set("web", "1");
    params.set("confirm", "1");
    return `/register/valve-body/${encodeURIComponent(String(tag || ""))}?${params.toString()}`;
  }, [tag, distributorMode, installingMode, launchedFromQr, fromAccount]);

  const isMobileBrowser = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod/i.test(String(navigator.userAgent || ""));
  }, []);

  const isIosBrowser = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod/i.test(String(navigator.userAgent || ""));
  }, []);

  const iosStoreUrl =
    process.env.REACT_APP_IOS_APP_STORE_URL ||
    "https://apps.apple.com/us/search?term=Valve%20Body%20Specialties";
  const androidStoreUrl =
    process.env.REACT_APP_ANDROID_PLAY_STORE_URL ||
    "https://play.google.com/store/apps/details?id=com.vbspecialties.app";
  const nativeScheme = process.env.REACT_APP_NATIVE_APP_SCHEME || "vbsnative";

  useEffect(() => {
    if (!tag || !launchedFromQr || redirectingTenant) return undefined;
    let cancelled = false;

    const resolveTarget = async () => {
      try {
        const response = await get(
          `/api/storefront/valve-bodies/${encodeURIComponent(
            String(tag)
          )}/registration-target`
        );
        if (cancelled) return;
        const target = response?.data || {};
        const rawTargetUrl = distributorMode
          ? target.distributorSoldUrl
          : target.registrationUrl;
        if (!rawTargetUrl) return;
        const nextUrl = new URL(rawTargetUrl, window.location.origin);
        if (distributorMode) nextUrl.searchParams.set("mode", "distributor-sold");
        if (installingMode) nextUrl.searchParams.set("mode", "installing");
        if (launchedFromQr) nextUrl.searchParams.set("qr", "1");
        if (forceWebMode) nextUrl.searchParams.set("web", "1");
        if (allowNativeLaunch) nextUrl.searchParams.set("native", "1");
        if (fromAccount) nextUrl.searchParams.set("from", "account");
        if (wantsConfirm) nextUrl.searchParams.set("confirm", "1");
        // Keep auth gate only for logged-out public QR; session users stay on confirm.
        if (wantsAuth && !hasSession) nextUrl.searchParams.set("auth", "1");
        if (hasSession && (wantsAuth || wantsConfirm)) {
          nextUrl.searchParams.set("confirm", "1");
          nextUrl.searchParams.delete("auth");
        }

        const currentHost = window.location.host.toLowerCase();
        const targetHost = nextUrl.host.toLowerCase();
        if (targetHost && targetHost !== currentHost) {
          setRedirectingTenant(true);
          window.location.replace(nextUrl.toString());
        }
      } catch {
        // Continue on the current storefront; the server-side company guard still protects writes.
      }
    };

    resolveTarget();
    return () => {
      cancelled = true;
    };
  }, [
    tag,
    launchedFromQr,
    distributorMode,
    installingMode,
    forceWebMode,
    allowNativeLaunch,
    fromAccount,
    wantsConfirm,
    wantsAuth,
    hasSession,
    redirectingTenant,
  ]);

  useEffect(() => {
    if (!tag || redirectingTenant || !qrLaunchMode || !isMobileBrowser) return undefined;

    const encodedTag = encodeURIComponent(String(tag));
    const modeSuffix = distributorMode ? "?mode=distributor-sold" : "";
    const deepLink = `${nativeScheme}://register/valve-body/${encodedTag}${modeSuffix}`;
    const storeUrl = isIosBrowser ? iosStoreUrl : androidStoreUrl;
    const intentUrl = `intent://register/valve-body/${encodedTag}${modeSuffix}#Intent;scheme=${nativeScheme};package=com.vbspecialties.app;S.browser_fallback_url=${encodeURIComponent(
      androidStoreUrl
    )};end`;

    let completed = false;
    setLaunchingNative(true);

    const onBackgrounded = () => {
      completed = true;
      setLaunchingNative(false);
    };

    const onVisibilityChange = () => {
      if (document.hidden) onBackgrounded();
    };

    window.addEventListener("pagehide", onBackgrounded);
    window.addEventListener("blur", onBackgrounded);
    document.addEventListener("visibilitychange", onVisibilityChange);

    const timer = window.setTimeout(() => {
      if (completed) return;
      setLaunchingNative(false);
      window.location.replace(storeUrl);
    }, 1700);

    if (isIosBrowser) {
      window.location.href = deepLink;
    } else {
      window.location.href = intentUrl;
    }

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pagehide", onBackgrounded);
      window.removeEventListener("blur", onBackgrounded);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    tag,
    qrLaunchMode,
    distributorMode,
    isMobileBrowser,
    isIosBrowser,
    iosStoreUrl,
    androidStoreUrl,
    nativeScheme,
    redirectingTenant,
  ]);

  // Session present (return from login OR already logged in from /m/account): strip auth gate.
  useEffect(() => {
    if (!hasSession || !tag || !hasIntent || wantsConfirm || !wantsAuth) return;
    const params = new URLSearchParams(searchParams);
    params.set("confirm", "1");
    params.delete("auth");
    params.set("web", "1");
    if (fromAccount) params.set("from", "account");
    setSearchParams(params, { replace: true });
  }, [
    hasSession,
    tag,
    hasIntent,
    wantsConfirm,
    wantsAuth,
    fromAccount,
    searchParams,
    setSearchParams,
  ]);

  const onLocaleChange = (next) => {
    const normalized = setTextLocale(next);
    setUiLocale(normalized);
  };

  const persistPending = () => {
    writePendingRegistration({
      tag: String(tag || ""),
      mode: distributorMode ? "distributor-sold" : "installing",
      form,
      locale: uiLocale,
      savedAt: Date.now(),
    });
  };

  const onSignIn = () => {
    persistPending();
    try {
      sessionStorage.setItem("postLoginNext", currentPath);
    } catch {
      // ignore
    }
    const params = new URLSearchParams({ next: currentPath });
    navigate(
      `${
        isMobileBrowser ? "/m/authentication/sign-in" : "/authentication/sign-in"
      }?${params.toString()}`
    );
  };

  const onCreateAccount = () => {
    persistPending();
    try {
      sessionStorage.setItem("postLoginNext", currentPath);
    } catch {
      // ignore
    }
    const params = new URLSearchParams();
    params.set("next", currentPath);
    params.set("accountType", distributorMode ? "distributor" : "customer");
    navigate(
      `${isMobileBrowser ? "/m/register" : "/register"}?${params.toString()}`
    );
  };

  const chooseIntent = (intentMode) => {
    const params = new URLSearchParams();
    params.set("mode", intentMode);
    if (launchedFromQr) params.set("qr", "1");
    if (fromAccount) params.set("from", "account");
    params.set("web", "1");
    navigate(
      `/register/valve-body/${encodeURIComponent(
        String(tag || "")
      )}?${params.toString()}`,
      { replace: true }
    );
  };

  const goBackToIntent = () => {
    const params = new URLSearchParams();
    if (launchedFromQr) params.set("qr", "1");
    if (fromAccount) params.set("from", "account");
    params.set("web", "1");
    navigate(
      `/register/valve-body/${encodeURIComponent(String(tag || ""))}?${params.toString()}`,
      { replace: true }
    );
  };

  const goToAuthOrConfirm = () => {
    if (installingMode) {
      const yearOk = String(form.vehicleYear || "").trim();
      const makeOk = String(form.vehicleMake || "").trim();
      const modelOk = String(form.vehicleModel || "").trim();
      if (!yearOk || !makeOk || !modelOk) {
        setMessage({
          type: "error",
          text: `${t("vehicleYear")}, ${t("vehicleMake")}, ${t("vehicleModel")} — ${t("required")}`,
        });
        return;
      }
    }
    setMessage(null);
    persistPending();
    const params = new URLSearchParams(searchParams);
    params.set("web", "1");
    if (fromAccount) params.set("from", "account");
    // Account hub / any existing session → confirm (no login or signup).
    if (hasSession) {
      params.set("confirm", "1");
      params.delete("auth");
    } else {
      params.set("auth", "1");
      params.delete("confirm");
    }
    setSearchParams(params, { replace: true });
  };

  const declineConfirm = () => {
    clearPendingRegistration();
    navigate(isMobileBrowser ? "/m/account" : "/account", { replace: true });
  };

  const onSubmit = async () => {
    if (!tag) return;
    setLoading(true);
    setMessage(null);
    try {
      const pending = readPendingRegistration();
      const payloadForm =
        pending?.tag && String(pending.tag) === String(tag) && pending.form
          ? { ...form, ...pending.form }
          : form;
      const res = distributorMode
        ? await post(
            `/api/storefront/valve-bodies/${encodeURIComponent(
              tag
            )}/distributor-sold`,
            { data: { identifier: tag } }
          )
        : await post("/api/storefront/valve-bodies/register-by-tag", {
            data: {
              identifier: tag,
              vehicleYear: Number(payloadForm.vehicleYear),
              vehicleMake: payloadForm.vehicleMake,
              vehicleModel: payloadForm.vehicleModel,
              vehicleTrim: payloadForm.vehicleTrim,
              vehicleVin: payloadForm.vehicleVin,
              notes: payloadForm.notes,
            },
          });
      clearPendingRegistration();
      setMessage({
        type: "success",
        text: distributorMode ? t("successSold") : t("successInstall"),
      });
      if (!distributorMode && res?.data?.valveBody?.documentId) {
        navigate(
          `${isMobileBrowser ? "/m" : ""}/account/valve-bodies/${
            res.data.valveBody.documentId
          }`,
          { replace: true }
        );
      } else if (distributorMode) {
        navigate(isMobileBrowser ? "/m/account" : "/account", { replace: true });
      }
    } catch (e) {
      setMessage({
        type: "error",
        text:
          e?.response?.data?.error?.message ||
          e?.message ||
          t("errorGeneric"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!tag) return <Navigate to="/" replace />;

  const canSubmit = !roleReady
    ? false
    : distributorMode
    ? isDistributor && capabilities.confirmDistributorSold !== false
    : isCustomer &&
      capabilities.submitWarranty !== false &&
      String(form.vehicleYear || "").trim() &&
      String(form.vehicleMake || "").trim() &&
      String(form.vehicleModel || "").trim();

  const detailsReady = distributorMode
    ? true
    : Boolean(
        String(form.vehicleYear || "").trim() &&
          String(form.vehicleMake || "").trim() &&
          String(form.vehicleModel || "").trim()
      );

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: COLORS.canvas,
    },
  };

  return (
    <StorefrontLayout maxWidth={null} hideHeader>
      <Box
        sx={{
          minHeight: "100dvh",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 2.25, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `radial-gradient(120% 80% at 50% -10%, rgba(181,71,8,0.12), transparent 55%), linear-gradient(180deg, ${COLORS.canvas} 0%, #EEF0F3 48%, ${COLORS.canvas} 100%)`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            p: { xs: 2.25, sm: 3 },
            borderRadius: 3.5,
            bgcolor: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            boxShadow: "0 24px 60px rgba(15,15,18,0.1)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: COLORS.accentSoft,
                color: COLORS.accent,
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {t("stepOf", { current: stepNumber, total: 2 })}
            </Box>
            <LanguageToggle
              locale={uiLocale}
              onChange={onLocaleChange}
              labels={{
                language: t("language"),
                english: t("english"),
                spanish: t("spanish"),
              }}
            />
          </Stack>

          <Box
            sx={{
              mb: 2.25,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              bgcolor: COLORS.canvas,
              border: `1px solid ${COLORS.line}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <MDTypography variant="caption" sx={{ color: COLORS.subtle, fontWeight: 700 }}>
              {t("reviewTag")}
            </MDTypography>
            <MDTypography
              variant="button"
              sx={{
                color: COLORS.ink,
                fontWeight: 900,
                fontFamily: 'ui-monospace, "SF Mono", monospace',
                letterSpacing: "0.02em",
              }}
            >
              {tag}
            </MDTypography>
          </Box>

          {message ? (
            <Alert severity={message.type} sx={{ mb: 2, borderRadius: 2 }}>
              {message.text}
            </Alert>
          ) : null}

          {launchingNative ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {t("nativeLaunch")}
            </Alert>
          ) : null}

          {phase === "session" ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: COLORS.accent }} />
            </Box>
          ) : null}

          {phase === "intent" ? (
            <>
              <MDTypography
                variant="h4"
                sx={{ color: COLORS.ink, fontWeight: 900, lineHeight: 1.1, mb: 0.75 }}
              >
                {t("step1Title")}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: COLORS.muted, mb: 2.5 }}>
                {t("step1Subtitle")}
              </MDTypography>
              <Box sx={{ display: "grid", gap: 1.25 }}>
                <ChoiceCard
                  title={t("installing")}
                  hint={t("installingHint")}
                  icon="build_circle"
                  onClick={() => chooseIntent("installing")}
                />
                <ChoiceCard
                  title={t("selling")}
                  hint={t("sellingHint")}
                  icon="storefront"
                  onClick={() => chooseIntent("distributor-sold")}
                />
              </Box>
            </>
          ) : null}

          {phase === "details" ? (
            <>
              <MDTypography
                variant="h4"
                sx={{ color: COLORS.ink, fontWeight: 900, lineHeight: 1.1, mb: 0.75 }}
              >
                {distributorMode ? t("step2SoldTitle") : t("step2InstallTitle")}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: COLORS.muted, mb: 2.25 }}>
                {distributorMode ? t("step2SoldSubtitle") : t("step2InstallSubtitle")}
              </MDTypography>

              {distributorMode ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: COLORS.successSoft,
                    border: "1px solid rgba(20,122,92,0.18)",
                    mb: 2.25,
                  }}
                >
                  <MDTypography variant="button" sx={{ color: COLORS.success, fontWeight: 800, display: "block", mb: 0.5 }}>
                    {t("reviewModeSold")}
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: COLORS.muted }}>
                    {t("confirmSoldHint")}
                  </MDTypography>
                </Box>
              ) : (
                <Box sx={{ display: "grid", gap: 1.5, mb: 2.25 }}>
                  <Box>
                    <FieldLabel>{t("vehicleYear")}</FieldLabel>
                    <MDInput
                      fullWidth
                      type="number"
                      inputProps={{ min: 1980, max: 2100 }}
                      value={form.vehicleYear}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, vehicleYear: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel>{t("vehicleMake")}</FieldLabel>
                    <MDInput
                      fullWidth
                      value={form.vehicleMake}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, vehicleMake: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel>{t("vehicleModel")}</FieldLabel>
                    <MDInput
                      fullWidth
                      value={form.vehicleModel}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, vehicleModel: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel optional optionalLabel={t("optional")}>
                      {t("vehicleTrim")}
                    </FieldLabel>
                    <MDInput
                      fullWidth
                      value={form.vehicleTrim}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, vehicleTrim: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel optional optionalLabel={t("optional")}>
                      {t("vehicleVin")}
                    </FieldLabel>
                    <MDInput
                      fullWidth
                      value={form.vehicleVin}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, vehicleVin: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <FieldLabel optional optionalLabel={t("optional")}>
                      {t("notes")}
                    </FieldLabel>
                    <MDInput
                      fullWidth
                      multiline
                      minRows={2}
                      value={form.notes}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      sx={inputSx}
                    />
                  </Box>
                </Box>
              )}

              <Stack direction="row" spacing={1.1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={goBackToIntent}
                  sx={{
                    flex: "0 0 auto",
                    borderRadius: 2,
                    py: 1.25,
                    borderColor: COLORS.line,
                    color: COLORS.ink,
                  }}
                >
                  {t("back")}
                </MDButton>
                <MDButton
                  fullWidth
                  onClick={goToAuthOrConfirm}
                  disabled={!detailsReady}
                  sx={{
                    borderRadius: 2,
                    py: 1.3,
                    fontWeight: 800,
                    bgcolor: COLORS.accent,
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": { bgcolor: COLORS.accentHover, boxShadow: "none" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(181,71,8,0.35)",
                      color: "#fff",
                    },
                  }}
                >
                  {t("continue")}
                </MDButton>
              </Stack>
            </>
          ) : null}

          {phase === "auth" ? (
            <>
              <MDTypography
                variant="h4"
                sx={{ color: COLORS.ink, fontWeight: 900, lineHeight: 1.1, mb: 0.75 }}
              >
                {t("authTitle")}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: COLORS.muted, mb: 2.5 }}>
                {distributorMode ? t("authSoldSubtitle") : t("authInstallSubtitle")}
              </MDTypography>
              <Box sx={{ display: "grid", gap: 1.15 }}>
                <MDButton
                  fullWidth
                  onClick={onSignIn}
                  sx={{
                    borderRadius: 2,
                    py: 1.35,
                    fontWeight: 800,
                    bgcolor: COLORS.accent,
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": { bgcolor: COLORS.accentHover, boxShadow: "none" },
                  }}
                >
                  {t("signIn")}
                </MDButton>
                <MDButton
                  fullWidth
                  variant="outlined"
                  onClick={onCreateAccount}
                  sx={{
                    borderRadius: 2,
                    py: 1.35,
                    fontWeight: 800,
                    borderColor: COLORS.accentBorder,
                    color: COLORS.accent,
                    bgcolor: COLORS.accentSoft,
                    "&:hover": {
                      borderColor: COLORS.accent,
                      bgcolor: "rgba(181,71,8,0.12)",
                    },
                  }}
                >
                  {distributorMode ? t("createDistributor") : t("createCustomer")}
                </MDButton>
                <MDButton
                  variant="text"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete("auth");
                    setSearchParams(params, { replace: true });
                  }}
                  sx={{ color: COLORS.muted, fontWeight: 700 }}
                >
                  {t("back")}
                </MDButton>
              </Box>
            </>
          ) : null}

          {phase === "confirm" ? (
            <>
              <MDTypography
                variant="h4"
                sx={{ color: COLORS.ink, fontWeight: 900, lineHeight: 1.15, mb: 0.75 }}
              >
                {t("confirmTitle")}
              </MDTypography>
              <MDTypography
                variant="body1"
                sx={{ color: COLORS.ink, fontWeight: 700, mb: 1, lineHeight: 1.35 }}
              >
                {distributorMode
                  ? t("confirmSold", { tag })
                  : t("confirmInstall", { tag })}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: COLORS.muted, mb: 2.25 }}>
                {distributorMode ? t("confirmSoldHint") : t("confirmInstallHint")}
              </MDTypography>

              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: COLORS.canvas,
                  border: `1px solid ${COLORS.line}`,
                  mb: 2.25,
                  display: "grid",
                  gap: 1,
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <MDTypography variant="caption" sx={{ color: COLORS.subtle, fontWeight: 700 }}>
                    {t("reviewTag")}
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    sx={{
                      color: COLORS.ink,
                      fontWeight: 900,
                      fontFamily: 'ui-monospace, "SF Mono", monospace',
                    }}
                  >
                    {tag}
                  </MDTypography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <MDTypography variant="caption" sx={{ color: COLORS.subtle, fontWeight: 700 }}>
                    {t("reviewMode")}
                  </MDTypography>
                  <MDTypography variant="button" sx={{ color: COLORS.ink, fontWeight: 800 }}>
                    {distributorMode ? t("reviewModeSold") : t("reviewModeInstall")}
                  </MDTypography>
                </Stack>
                {!distributorMode ? (
                  <>
                    <Divider />
                    <MDTypography variant="body2" sx={{ color: COLORS.muted }}>
                      {[form.vehicleYear, form.vehicleMake, form.vehicleModel]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </MDTypography>
                  </>
                ) : null}
              </Box>

              {!roleReady ? (
                <Box sx={{ display: "grid", placeItems: "center", py: 2, mb: 1 }}>
                  <CircularProgress size={22} sx={{ color: COLORS.accent }} />
                </Box>
              ) : distributorMode ? (
                !isDistributor ? (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    {t("wrongDistributor")}
                  </Alert>
                ) : capabilities.confirmDistributorSold === false ? (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    {t("blockedDistributor")}
                  </Alert>
                ) : null
              ) : !isCustomer ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  {t("wrongCustomer")}
                </Alert>
              ) : capabilities.submitWarranty === false ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  {t("blockedCustomer")}
                </Alert>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
                <MDButton
                  fullWidth
                  variant="outlined"
                  onClick={declineConfirm}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    py: 1.35,
                    fontWeight: 800,
                    borderColor: "rgba(165,24,18,0.25)",
                    color: COLORS.danger,
                    bgcolor: COLORS.dangerSoft,
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  {t("no")}
                </MDButton>
                <MDButton
                  fullWidth
                  onClick={onSubmit}
                  disabled={!canSubmit || loading}
                  sx={{
                    borderRadius: 2,
                    py: 1.35,
                    fontWeight: 800,
                    bgcolor: COLORS.accent,
                    color: "#fff",
                    boxShadow: "none",
                    order: { xs: 1, sm: 2 },
                    "&:hover": { bgcolor: COLORS.accentHover, boxShadow: "none" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(181,71,8,0.35)",
                      color: "#fff",
                    },
                  }}
                >
                  {loading
                    ? distributorMode
                      ? t("saving")
                      : t("registering")
                    : distributorMode
                    ? t("yesSold")
                    : t("yes")}
                </MDButton>
              </Stack>
            </>
          ) : null}
        </Box>
      </Box>
    </StorefrontLayout>
  );
}
